import { useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { allArticles, getArticleBySlug, allArticleImages } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const effectiveSlug = slug || location.pathname.replace(/^\//, "").replace(/\/$/, "");
  const article = getArticleBySlug(effectiveSlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [effectiveSlug]);

  if (!article) {
    return (
      <main className="bg-background-50 min-h-screen">
        <Navbar />
        <section className="pt-24 md:pt-32 pb-12 md:pb-16">
          <div className="px-4 md:px-6 max-w-5xl mx-auto text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700">
              <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const articleImage = allArticleImages[article.slug];

  return (
    <main className="bg-background-50">
      <Navbar />

      {/* Article Header */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-accent-100/50">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <p className="text-xs md:text-sm text-foreground-500 font-label tracking-wider uppercase mb-4">
            / SEO Guide
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="lg:max-w-3xl">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
                {article.h1}
              </h1>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-accent-300 border-2 border-accent-100/50 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20with%20warm%20studio%20lighting%2C%20light%20gray%20background%2C%20editorial%20style&width=80&height=80&seq=author-avatar-v2-1&orientation=squarish"
                    alt="Editorial team member"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-accent-300 border-2 border-accent-100/50 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20with%20natural%20window%20light%2C%20warm%20indoor%20background%2C%20editorial%20style&width=80&height=80&seq=author-avatar-v2-2&orientation=squarish"
                    alt="Editorial team member"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-accent-300 border-2 border-accent-100/50 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20with%20soft%20studio%20lighting%2C%20neutral%20backdrop%2C%20editorial%20style&width=80&height=80&seq=author-avatar-v2-3&orientation=squarish"
                    alt="Editorial team member"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-accent-300 border-2 border-accent-100/50 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20with%20warm%20lighting%2C%20clean%20background%2C%20editorial%20style&width=80&height=80&seq=author-avatar-v2-4&orientation=squarish"
                    alt="Editorial team member"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div>
                <p className="text-xs text-foreground-500">By</p>
                <p className="text-sm font-semibold text-foreground-800">
                  ACBuy Editorial Team
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-3 bg-background-50 border-b border-background-200/70">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap">
            <Link
              to="/"
              className="hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Home
            </Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
            <Link
              to="/"
              className="hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Guides
            </Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">
              {article.title}
            </span>
          </nav>
        </div>
      </section>

      {/* Article Body */}
      <section className="py-12 md:py-16">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
            {/* Side Image */}
            <div className="lg:w-2/5 flex-shrink-0">
              <div className="rounded-2xl overflow-hidden bg-primary-100 sticky top-24">
                <img
                  src={articleImage || ""}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            {/* Content */}
            <div className="lg:w-3/5">
              <p className="text-xs md:text-sm text-foreground-500 font-label mb-6">
                (SEO Optimized &bull; Updated June 2026 &bull; {Math.max(3, Math.ceil(article.sections.reduce((acc, s) => acc + s.content.join(" ").split(" ").length, 0) / 200))} min read)
              </p>

              {article.sections.map((section, idx) => (
                <article key={idx} className="mb-10 md:mb-14">
                  <h4 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">
                    <a id={section.heading.toLowerCase().replace(/\s+/g, "-")}>
                      {section.heading}
                    </a>
                  </h4>
                  {section.content.map((paragraph, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5"
                    >
                      {paragraph}
                    </p>
                  ))}

                  {/* Internal link injection */}
                  {idx === 0 && (
                    <p className="text-sm md:text-base text-foreground-600 mb-5 leading-relaxed">
                      To dive deeper into the full ecosystem, explore our{" "}
                      <Link
                        to="/"
                        className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2"
                      >
                        complete acbuyspreadsheet guide
                      </Link>{" "}
                      with detailed comparisons and workflow strategies, or
                      check out the{" "}
                      <a
                        href="https://kaspreadsheet.com/"
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2"
                      >
                        main ACBuy Spreadsheet system
                      </a>{" "}
                      to see the tools in action.
                    </p>
                  )}

                  {idx === Math.floor(article.sections.length / 2) && (
                    <div className="my-6 p-5 rounded-xl bg-background-100 border border-background-200/70">
                      <p className="text-sm font-semibold text-foreground-900 mb-2">
                        Key Takeaway
                      </p>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        The ACBuySpreadsheet system delivers significant efficiency gains for users who need structured comparison frameworks. For more strategies, visit our{" "}
                        <Link
                          to="/"
                          className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2"
                        >
                          spreadsheet comparison system
                        </Link>{" "}
                        overview.
                      </p>
                    </div>
                  )}

                  {idx === article.sections.length - 1 && (
                    <p className="text-sm md:text-base text-foreground-600 mb-5 leading-relaxed">
                      Ready to get started? Head over to our{" "}
                      <Link
                        to="/"
                        className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2"
                      >
                        acbuyspreadsheet hub homepage
                      </Link>{" "}
                      for the complete collection of guides, comparisons, and
                      resources, or browse the{" "}
                      <a
                        href="https://kaspreadsheet.com/category-911-Shoes.html"
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2"
                      >
                        product categories
                      </a>{" "}
                      on the main platform.
                    </p>
                  )}
                </article>
              ))}

              {/* FAQ Section */}
              {article.faqs.length > 0 && (
                <section className="mt-12 pt-10 border-t border-background-200/70">
                  <h4 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
                    <a id="frequently-asked-questions">
                      Frequently Asked Questions About ACBuySpreadsheet
                    </a>
                  </h4>

                  <div className="space-y-4">
                    {article.faqs.map((faq, idx) => (
                      <details
                        key={idx}
                        className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
                      >
                        <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                          <h5 className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                            {faq.question}
                          </h5>
                          <i className="ri-add-line text-foreground-500 group-open:hidden w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                          <i className="ri-subtract-line text-foreground-500 hidden group-open:block w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                        </summary>
                        <div className="px-5 pb-4">
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {/* Back to Home */}
              <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                  Back to ACBuySpreadsheet Home
                </Link>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Access Full System
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Structured Data - FAQPage Schema */}
      {article.faqs.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: article.faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.answer,
                },
              })),
            }),
          }}
        />
      )}

      {/* Structured Data - BlogPosting Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": article.h1,
            "description": article.excerpt,
            "url": `${import.meta.env.VITE_SITE_URL || "https://acbuyspreadsheet.delivery"}/${article.slug}`,
            "datePublished": "2026-06-24",
            "dateModified": "2026-06-24",
            "author": {
              "@type": "Organization",
              "name": "ACBuySpreadsheet Editorial Team",
              "url": import.meta.env.VITE_SITE_URL || "https://acbuyspreadsheet.delivery",
            },
            "publisher": {
              "@type": "Organization",
              "name": "ACBuySpreadsheet Hub",
              "url": import.meta.env.VITE_SITE_URL || "https://acbuyspreadsheet.delivery",
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `${import.meta.env.VITE_SITE_URL || "https://acbuyspreadsheet.delivery"}/${article.slug}`,
            },
            "inLanguage": "en",
          }),
        }}
      />

      <Footer />
    </main>
  );
}